/**
 * W-Sidecart JavaScript
 * Handles cart drawer functionality, quantity updates, cross-sell, and Swiper integration
 */

const wSidecartLocale = () => (document.documentElement.lang || 'fr').toLowerCase().split('-')[0];
const wSidecartText = (key) => {
  const translations = {
    fr: { empty: 'Votre panier est vide', discover: 'Découvrir nos produits', unavailableProduct: 'Ce produit est indisponible', free: 'OFFERT' },
    en: { empty: 'Your cart is empty', discover: 'Discover our products', unavailableProduct: 'This product is unavailable', free: 'FREE' },
    de: { empty: 'Dein Warenkorb ist leer', discover: 'Unsere Produkte entdecken', unavailableProduct: 'Dieses Produkt ist nicht verfügbar', free: 'GRATIS' },
    es: { empty: 'Tu carrito está vacío', discover: 'Descubre nuestros productos', unavailableProduct: 'Este producto no está disponible', free: 'GRATIS' },
    it: { empty: 'Il tuo carrello è vuoto', discover: 'Scopri i nostri prodotti', unavailableProduct: 'Questo prodotto non è disponibile', free: 'OMAGGIO' }
  };
  return (translations[wSidecartLocale()] || translations.fr)[key];
};

class WSidecart {
  constructor() {
    this.sidecart = document.querySelector('[data-sidecart]');
    if (!this.sidecart) return;

    this.overlay = this.sidecart.querySelector('[data-sidecart-overlay]');
    this.panel = this.sidecart.querySelector('.sidecart__panel');
    this.content = this.sidecart.querySelector('[data-sidecart-content]');
    this.crosssellContainer = this.sidecart.querySelector('[data-sidecart-crosssell]');

    this.config = window.sidecartConfig || {
      freeShippingThreshold: 6000,
      giftThreshold: 10000,
      crossSellCount: 4,
      routes: {
        cart: '/cart',
        cartAdd: '/cart/add.js',
        cartChange: '/cart/change.js',
        checkout: '/checkout',
        recommendations: '/recommendations/products'
      },
      moneyFormat: '{{amount}}€'
    };

    this.swiper = null;
    this.isLoading = false;
    this.isHandlingGifts = false;
    this.isDirty = false;
    this.refreshRequestId = 0;
    this.recommendationsRequestId = 0;
    this.lastProductIdForRecommendations = null;
    this.loadedRecommendationsProductId = null;
    this.loadingRecommendationsProductId = null;
    this.currentCart = null;

    this.init();
  }

  init() {
    this.bindEvents();
    this.initSwiper();

    // Store initial product ID from DOM if available
    const recommendationsEl = this.sidecart.querySelector('[data-product-id]');
    if (recommendationsEl && recommendationsEl.dataset.productId) {
      this.lastProductIdForRecommendations = recommendationsEl.dataset.productId;
    }

    this.loadRecommendations(this.lastProductIdForRecommendations);
  }

  bindEvents() {
    // Close button
    const closeBtn = this.sidecart.querySelector('[data-sidecart-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Quantity buttons
    this.sidecart.addEventListener('click', (e) => {
      const minusBtn = e.target.closest('[data-sidecart-quantity-minus]');
      const plusBtn = e.target.closest('[data-sidecart-quantity-plus]');
      const removeBtn = e.target.closest('[data-sidecart-remove]');
      const crosssellAddBtn = e.target.closest('[data-crosssell-add]');

      if (minusBtn) {
        e.preventDefault();
        const key = minusBtn.dataset.sidecartQuantityMinus;
        this.updateQuantity(key, -1);
      }

      if (plusBtn) {
        e.preventDefault();
        const key = plusBtn.dataset.sidecartQuantityPlus;
        this.updateQuantity(key, 1);
      }

      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.sidecartRemove;
        this.removeItem(key);
      }

      if (crosssellAddBtn) {
        e.preventDefault();
        const variantId = crosssellAddBtn.dataset.crosssellAdd;
        const productId = crosssellAddBtn.dataset.productId;
        this.addCrosssellItem(variantId, productId, crosssellAddBtn);
      }
    });

    // Cross-sell variant change
    this.sidecart.addEventListener('change', (e) => {
      const variantSelect = e.target.closest('[data-crosssell-variant]');
      if (variantSelect) {
        const productId = variantSelect.dataset.crosssellVariant;
        const addBtn = this.sidecart.querySelector(`[data-crosssell-add][data-product-id="${productId}"]`);
        if (addBtn) {
          addBtn.dataset.crosssellAdd = variantSelect.value;
        }
      }
    });

    // Reuse cart state already returned by other cart mutations.
    document.addEventListener('cart:updated', (event) => {
      if (event.detail?.skipSidecartRefresh) return;
      this.handleExternalCartUpdate(event.detail?.cart).catch((error) => {
        console.error('Error synchronizing sidecart:', error);
      });
    });

    // The full cart includes w-sidecart-content in its bundled change response.
    document.addEventListener('line-item:change:end', (event) => {
      this.handleExternalCartUpdate(event.detail?.cart).catch((error) => {
        console.error('Error synchronizing sidecart from full cart:', error);
      });
    });

    // cart:item-added is intentionally not used as a second refresh trigger.
    // Add flows either call openSidecart() (which refreshes by default) or
    // dispatch cart:updated once.

    // Listen for cart-drawer:open to sync with existing theme behavior
    document.addEventListener('cart-drawer:open', () => {
      // Le sidecart est déjà ouvert via openCartDrawer dans pleiades.js
    });
  }

  isOpen() {
    return this.sidecart.getAttribute('aria-hidden') === 'false';
  }

  open() {
    this.sidecart.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sidecart-open');
    document.body.classList.add('sidecart-open');

    // Focus trap
    this.panel.focus();
  }

  close() {
    this.sidecart.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('sidecart-open');
    document.body.classList.remove('sidecart-open');
  }

  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  setLoadingState(isLoading, loadingItem = null) {
    this.isLoading = isLoading;

    // Disable/enable all buttons in the sidecart
    const allButtons = this.sidecart.querySelectorAll('button, .button, .button--primary, .button--cta, .button--primary-small');
    allButtons.forEach(btn => {
      btn.disabled = isLoading;
    });

    // Also disable checkout link
    const checkoutLink = this.sidecart.querySelector('.sidecart__checkout');
    if (checkoutLink) {
      if (isLoading) {
        checkoutLink.style.pointerEvents = 'none';
        checkoutLink.style.opacity = '0.5';
      } else {
        checkoutLink.style.pointerEvents = '';
        checkoutLink.style.opacity = '';
      }
    }

    // Set loading state on specific item
    if (loadingItem) {
      if (isLoading) {
        loadingItem.setAttribute('data-loading', 'true');
      } else {
        loadingItem.removeAttribute('data-loading');
      }
    }
  }

  getCartItemByKey(cart, key) {
    if (!this.isCartState(cart)) return null;
    return cart.items.find((cartItem) => cartItem.key === key) || null;
  }

  buildOptimisticCart(cart, key, newQty) {
    const cartItem = this.getCartItemByKey(cart, key);
    if (!cartItem || cartItem.quantity <= 0) return null;

    const quantityDelta = newQty - cartItem.quantity;
    const finalUnitPrice = typeof cartItem.final_price === 'number'
      ? cartItem.final_price
      : Math.round(cartItem.final_line_price / cartItem.quantity);
    const originalUnitPrice = typeof cartItem.original_price === 'number'
      ? cartItem.original_price
      : Math.round(cartItem.original_line_price / cartItem.quantity);
    const lineUnitPrice = typeof cartItem.price === 'number'
      ? cartItem.price
      : finalUnitPrice;

    const nextItem = {
      ...cartItem,
      quantity: newQty,
      final_line_price: finalUnitPrice * newQty,
      original_line_price: originalUnitPrice * newQty,
      line_price: lineUnitPrice * newQty
    };

    const finalLineDelta = nextItem.final_line_price - cartItem.final_line_price;
    const originalLineDelta = nextItem.original_line_price - cartItem.original_line_price;

    return {
      ...cart,
      items: cart.items.map((item) => item.key === key ? nextItem : item),
      item_count: Math.max(0, cart.item_count + quantityDelta),
      total_price: Math.max(0, cart.total_price + finalLineDelta),
      original_total_price: typeof cart.original_total_price === 'number'
        ? Math.max(0, cart.original_total_price + originalLineDelta)
        : cart.original_total_price,
      items_subtotal_price: typeof cart.items_subtotal_price === 'number'
        ? Math.max(0, cart.items_subtotal_price + finalLineDelta)
        : cart.items_subtotal_price
    };
  }

  renderOptimisticLinePrice(itemElement, cartItem) {
    if (!itemElement || !cartItem) return;

    const priceElement = itemElement.querySelector('.sidecart__product-price:not(.sidecart__product-price--gift)');
    if (!priceElement) return;

    if (cartItem.original_line_price !== cartItem.final_line_price) {
      priceElement.innerHTML = `<del>${this.formatMoney(cartItem.original_line_price)}</del><ins>${this.formatMoney(cartItem.final_line_price)}</ins>`;
    } else {
      priceElement.textContent = this.formatMoney(cartItem.final_line_price);
    }
  }

  createOptimisticGiftElement(step) {
    const productsElement = this.sidecart.querySelector('[data-sidecart-products]');
    if (!productsElement || !step?.giftProduct) return null;

    const giftElement = document.createElement('div');
    giftElement.className = 'sidecart__product sidecart__product--gift';
    giftElement.setAttribute('data-sidecart-item', '');
    giftElement.setAttribute('data-gift', 'true');
    giftElement.setAttribute('data-optimistic-gift', step.id);

    const imageWrapper = document.createElement('div');
    imageWrapper.className = 'sidecart__product-image';

    if (step.giftProduct.image) {
      const image = document.createElement('img');
      image.src = step.giftProduct.image;
      image.alt = step.giftProduct.title || step.label || '';
      image.width = 72;
      image.height = 72;
      image.loading = 'eager';
      imageWrapper.appendChild(image);
    }

    const info = document.createElement('div');
    info.className = 'sidecart__product-info';

    const details = document.createElement('div');
    details.className = 'sidecart__product-details';

    const header = document.createElement('div');
    header.className = 'sidecart__product-header';

    const title = document.createElement('span');
    title.className = 'sidecart__product-title p-medium';
    title.textContent = step.giftProduct.title || step.label || '';
    header.appendChild(title);

    const options = document.createElement('div');
    options.className = 'sidecart__product-options p-medium';

    const option = document.createElement('p');
    option.className = 'sidecart__product-option sidecart__product-option--gift';
    option.textContent = step.label || '';
    options.appendChild(option);

    details.appendChild(header);
    details.appendChild(options);

    const footer = document.createElement('div');
    footer.className = 'sidecart__product-footer';

    const price = document.createElement('p');
    price.className = 'sidecart__product-price sidecart__product-price--gift p-medium';
    price.textContent = wSidecartText('free');
    footer.appendChild(price);

    info.appendChild(details);
    info.appendChild(footer);

    giftElement.appendChild(imageWrapper);
    giftElement.appendChild(info);
    productsElement.prepend(giftElement);

    return giftElement;
  }

  clearOptimisticGiftPreview() {
    this.sidecart.querySelectorAll('[data-optimistic-gift]').forEach((element) => element.remove());
    this.sidecart.querySelectorAll('[data-optimistic-hidden-gift]').forEach((element) => {
      element.style.display = '';
      element.removeAttribute('data-optimistic-hidden-gift');
    });
  }

  syncOptimisticGiftPreview(cart) {
    if (!this.isCartState(cart)) return;

    const totalWithoutGifts = this.calculateTotalWithoutGifts(cart);
    const giftSteps = (this.config.steps || []).filter((step) => step.giftProduct);

    giftSteps.forEach((step) => {
      const giftInCart = cart.items.find((cartItem) =>
        cartItem.variant_id === step.giftProduct.variantId &&
        cartItem.properties &&
        cartItem.properties._gift === step.id
      );

      const realGiftElement = giftInCart
        ? this.sidecart.querySelector('[data-sidecart-item][data-key="' + giftInCart.key + '"]')
        : null;
      const optimisticGiftElement = this.sidecart.querySelector('[data-optimistic-gift="' + step.id + '"]');
      const thresholdReached = totalWithoutGifts >= step.threshold;

      if (thresholdReached) {
        if (realGiftElement) {
          realGiftElement.style.display = '';
          realGiftElement.removeAttribute('data-optimistic-hidden-gift');
          optimisticGiftElement?.remove();
        } else if (!optimisticGiftElement) {
          this.createOptimisticGiftElement(step);
        }
        return;
      }

      optimisticGiftElement?.remove();

      if (realGiftElement) {
        realGiftElement.style.display = 'none';
        realGiftElement.setAttribute('data-optimistic-hidden-gift', '');
      }
    });
  }

  applyOptimisticQuantity(key, newQty) {
    const itemElement = this.sidecart.querySelector(`[data-sidecart-item][data-key="${key}"]`);
    const quantityElement = itemElement?.querySelector(`[data-sidecart-quantity-value="${key}"]`);
    const previousCart = this.currentCart;

    if (quantityElement) {
      quantityElement.textContent = newQty;
    }

    const optimisticCart = this.buildOptimisticCart(previousCart, key, newQty);
    if (!optimisticCart) {
      return { previousCart, key };
    }

    const optimisticItem = this.getCartItemByKey(optimisticCart, key);
    this.renderOptimisticLinePrice(itemElement, optimisticItem);

    const totalElement = this.sidecart.querySelector('[data-sidecart-total]');
    if (totalElement) {
      totalElement.textContent = this.formatMoney(optimisticCart.total_price);
    }

    this.updateProgressBars(this.calculateTotalWithoutGifts(optimisticCart));
    this.syncOptimisticGiftPreview(optimisticCart);
    this.currentCart = optimisticCart;

    return { previousCart, key };
  }

  restoreOptimisticQuantity(snapshot) {
    if (!snapshot?.previousCart || !this.isCartState(snapshot.previousCart)) return;

    const previousCart = snapshot.previousCart;
    const previousItem = this.getCartItemByKey(previousCart, snapshot.key);
    const itemElement = this.sidecart.querySelector(`[data-sidecart-item][data-key="${snapshot.key}"]`);

    if (previousItem && itemElement) {
      const quantityElement = itemElement.querySelector(`[data-sidecart-quantity-value="${snapshot.key}"]`);
      if (quantityElement) {
        quantityElement.textContent = previousItem.quantity;
      }
      this.renderOptimisticLinePrice(itemElement, previousItem);
    }

    const totalElement = this.sidecart.querySelector('[data-sidecart-total]');
    if (totalElement) {
      totalElement.textContent = this.formatMoney(previousCart.total_price);
    }

    this.updateProgressBars(this.calculateTotalWithoutGifts(previousCart));
    this.syncOptimisticGiftPreview(previousCart);
    this.currentCart = previousCart;
  }

  setOptimisticPendingState(isPending, itemElement = null) {
    this.isLoading = isPending;

    if (itemElement) {
      itemElement.querySelectorAll('[data-sidecart-quantity-minus], [data-sidecart-quantity-plus]').forEach((button) => {
        button.disabled = isPending;
      });
    }

    const checkoutLink = this.sidecart.querySelector('.sidecart__checkout');
    if (checkoutLink) {
      checkoutLink.style.pointerEvents = isPending ? 'none' : '';
    }
  }

  async updateQuantity(key, change) {
    if (this.isLoading) return;

    const item = this.sidecart.querySelector(`[data-sidecart-item][data-key="${key}"]`);
    if (!item) return;

    const quantityValue = item.querySelector(`[data-sidecart-quantity-value="${key}"]`);
    const currentQty = parseInt(quantityValue.textContent, 10);
    const newQty = Math.max(0, currentQty + change);

    if (newQty === 0) {
      this.removeItem(key);
      return;
    }

    const optimisticSnapshot = this.applyOptimisticQuantity(key, newQty);
    this.setOptimisticPendingState(true, item);
    this.refreshRequestId += 1;

    let confirmedCart = null;

    try {
      confirmedCart = await this.requestChange(key, newQty);
      const finalCart = await this.finalizeCartState(confirmedCart, { renderContent: true });
      this.dispatchCartUpdated(finalCart);
    } catch (error) {
      if (!confirmedCart) {
        this.restoreOptimisticQuantity(optimisticSnapshot);
      } else {
        this.clearOptimisticGiftPreview();
        const sectionHtml = confirmedCart?.sections?.['w-sidecart-content'] || null;
        const contentFresh = sectionHtml ? this.renderSidecartContent(sectionHtml) : false;
        this.syncFromCart(confirmedCart, contentFresh);
      }

      console.error('Error updating quantity:', error);
      this.isDirty = true;
    } finally {
      this.setOptimisticPendingState(false, item);
    }
  }
  async removeItem(key) {
    if (this.isLoading) return;

    const item = this.sidecart.querySelector(`[data-sidecart-item][data-key="${key}"]`);
    if (!item) return;

    this.setLoadingState(true, item);
    this.refreshRequestId += 1;

    try {
      const cart = await this.requestChange(key, 0);
      const finalCart = await this.finalizeCartState(cart, { renderContent: true });
      this.dispatchCartUpdated(finalCart);
    } catch (error) {
      console.error('Error removing item:', error);
      this.isDirty = true;
    } finally {
      this.setLoadingState(false);
    }
  }
  async addCrosssellItem(variantId, productId, button) {
    if (this.isLoading) return;

    this.setLoadingState(true);
    const originalText = button.textContent;
    button.textContent = '...';
    this.refreshRequestId += 1;

    try {
      const cart = await this.requestAddAndCart([{
        id: parseInt(variantId, 10),
        quantity: 1
      }]);

      const finalCart = await this.finalizeCartState(cart, { renderContent: true });
      this.dispatchCartUpdated(finalCart);
    } catch (error) {
      console.error('Error adding crosssell item:', error);
      button.textContent = wSidecartText('unavailableProduct');
      setTimeout(() => { button.textContent = originalText; }, 2000);
      this.isDirty = true;
      return;
    } finally {
      this.setLoadingState(false);
      if (button.textContent === '...') {
        button.textContent = originalText;
      }
    }
  }
  isCartState(cart) {
    return Boolean(
      cart &&
      Array.isArray(cart.items) &&
      typeof cart.item_count === 'number' &&
      typeof cart.total_price === 'number'
    );
  }

  getMutationSections() {
    const sections = ['w-sidecart-content'];

    if (document.getElementById('Cart')) {
      sections.push('main-cart');
    }

    if (document.getElementById('cart-drawer-toggle')) {
      sections.push('cart-bubble');
    }

    return sections;
  }

  queueCartMutation(task) {
    window.sublimaCartStateVersion = (window.sublimaCartStateVersion || 0) + 1;
    const previous = window.sublimaCartMutationQueue || Promise.resolve();
    const run = previous.then(task, task);
    window.sublimaCartMutationQueue = run.catch(() => {});
    return run;
  }

  async requestChange(id, quantity) {
    return this.queueCartMutation(async () => {
      const response = await fetch(this.config.routes.cartChange, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id,
          quantity,
          sections: this.getMutationSections(),
          sections_url: window.location.pathname
        })
      });

      const cart = await response.json().catch(() => ({}));
      if (!response.ok || cart.errors || cart.status) {
        const message = cart.errors || cart.description || cart.message || wSidecartText('unavailableProduct');
        throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
      }

      return cart;
    });
  }

  async requestAddAndCart(items) {
    return this.queueCartMutation(async () => {
      const response = await fetch(this.config.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items,
          sections: this.getMutationSections(),
          sections_url: window.location.pathname
        })
      });

      const addedState = await response.json().catch(() => ({}));
      if (!response.ok || addedState.errors || addedState.status) {
        const message = addedState.errors || addedState.description || addedState.message || wSidecartText('unavailableProduct');
        throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
      }

      const cart = await this.fetchCartData();
      if (addedState.sections) {
        cart.sections = addedState.sections;
      }

      return cart;
    });
  }

  async fetchCartData() {
    const response = await fetch(`${this.config.routes.root}cart.js`, {
      headers: { 'Accept': 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Cart fetch failed (${response.status})`);
    }

    return response.json();
  }

  async fetchSidecartSection() {
    const response = await fetch(`${this.config.routes.root}?sections=w-sidecart-content`);

    if (!response.ok) {
      throw new Error(`Sidecart section refresh failed (${response.status})`);
    }

    const data = await response.json();
    return data['w-sidecart-content'] || null;
  }

  renderSidecartContent(html) {
    if (!html || !this.content) return false;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newContent = doc.querySelector('[data-sidecart-content]');

    if (!newContent) return false;

    this.content.innerHTML = newContent.innerHTML;
    this.isDirty = false;
    return true;
  }

  syncHeaderCartCount(itemCount) {
    const headerTriggers = document.querySelectorAll('[data-open-sidecart]');

    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      if (itemCount > 0) {
        el.textContent = itemCount;
        el.style.display = '';
      } else if (el.closest('[data-open-sidecart]')) {
        el.remove();
      } else {
        el.textContent = '0';
        el.style.display = 'none';
      }
    });

    if (itemCount <= 0) return;

    headerTriggers.forEach((trigger) => {
      if (trigger.querySelector('[data-cart-count]')) return;

      const count = document.createElement('span');
      count.className = 'header__cart-count';
      count.setAttribute('data-cart-count', '');
      count.textContent = itemCount;
      trigger.appendChild(count);
    });
  }

  syncFromCart(cart, syncContentState = true) {
    if (!this.isCartState(cart)) return;

    this.currentCart = cart;
    const totalWithoutGifts = this.calculateTotalWithoutGifts(cart);

    const countEl = this.sidecart.querySelector('[data-sidecart-count]');
    if (countEl) {
      countEl.textContent = cart.item_count;
    }

    this.syncHeaderCartCount(cart.item_count);

    const totalEl = this.sidecart.querySelector('[data-sidecart-total]');
    if (totalEl) {
      totalEl.textContent = this.formatMoney(cart.total_price);
    }

    this.updateProgressBars(totalWithoutGifts);

    if (syncContentState) {
      const footer = this.sidecart.querySelector('[data-sidecart-footer]');
      if (footer) {
        footer.style.display = cart.item_count > 0 ? '' : 'none';
      }

      this.updateEmptyState(cart.item_count);
    }

    if (cart.items.length > 0) {
      this.lastProductIdForRecommendations = cart.items[0].product_id;
    }
  }

  async finalizeCartState(cart, { renderContent = this.isOpen(), handleGifts = true } = {}) {
    let finalCart = cart;

    if (!this.isCartState(finalCart)) {
      finalCart = await this.fetchCartData();
    }

    if (handleGifts) {
      const giftResult = await this.handleGiftProducts(finalCart);
      finalCart = giftResult.cart;
    }

    let contentFresh = false;

    if (renderContent) {
      let sectionHtml = finalCart?.sections?.['w-sidecart-content'] || null;

      if (!sectionHtml) {
        sectionHtml = await this.fetchSidecartSection();
      }

      contentFresh = this.renderSidecartContent(sectionHtml);
      if (!contentFresh) {
        this.isDirty = true;
      }
    } else {
      this.isDirty = true;
    }

    this.syncFromCart(finalCart, contentFresh);

    if (renderContent) {
      const productId = finalCart.items.length > 0
        ? finalCart.items[0].product_id
        : this.lastProductIdForRecommendations;
      this.loadRecommendations(productId);
    }

    return finalCart;
  }

  dispatchCartUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {
        cart,
        skipSidecartRefresh: true
      }
    }));
  }

  async handleExternalCartUpdate(cart) {
    this.refreshRequestId += 1;

    let currentCart = this.isCartState(cart) ? cart : await this.fetchCartData();
    const giftResult = await this.handleGiftProducts(currentCart);
    currentCart = giftResult.cart;

    if (this.isOpen()) {
      await this.finalizeCartState(currentCart, {
        renderContent: true,
        handleGifts: false
      });
    } else {
      this.isDirty = true;
      this.syncFromCart(currentCart, false);
    }

    if (giftResult.changed) {
      this.dispatchCartUpdated(currentCart);
    }

    return currentCart;
  }

  async refresh(skipGifts = false) {
    const requestId = ++this.refreshRequestId;

    try {
      await (window.sublimaCartMutationQueue || Promise.resolve()).catch(() => {});
      const mutationVersion = window.sublimaCartStateVersion || 0;
      let cart = await this.fetchCartData();

      if (
        requestId !== this.refreshRequestId ||
        mutationVersion !== (window.sublimaCartStateVersion || 0)
      ) {
        this.isDirty = true;
        return null;
      }

      if (!skipGifts) {
        const giftResult = await this.handleGiftProducts(cart);
        cart = giftResult.cart;
      }

      let sectionHtml = cart?.sections?.['w-sidecart-content'] || null;

      if (!sectionHtml) {
        const sectionMutationVersion = window.sublimaCartStateVersion || 0;
        sectionHtml = await this.fetchSidecartSection();

        if (
          requestId !== this.refreshRequestId ||
          sectionMutationVersion !== (window.sublimaCartStateVersion || 0)
        ) {
          this.isDirty = true;
          return null;
        }
      }

      if (!this.renderSidecartContent(sectionHtml)) {
        this.isDirty = true;
      }

      this.syncFromCart(cart, !this.isDirty);

      const productId = cart.items.length > 0
        ? cart.items[0].product_id
        : this.lastProductIdForRecommendations;
      this.loadRecommendations(productId);

      return cart;
    } catch (error) {
      console.error('Error refreshing sidecart:', error);
      this.isDirty = true;
      return null;
    }
  }

  /**
   * Calculate cart total excluding gift products
   */
  calculateTotalWithoutGifts(cart) {
    let total = 0;
    cart.items.forEach(item => {
      // Exclude items with _gift property
      const isGift = item.properties && item.properties._gift;
      if (!isGift) {
        total += item.final_line_price;
      }
    });
    return total;
  }

  /**
   * Handle automatic gift product add/remove based on thresholds
   */
  async handleGiftProducts(cart) {
    if (this.isHandlingGifts) {
      return { cart, changed: false };
    }

    this.isHandlingGifts = true;

    let currentCart = cart;
    let changed = false;

    try {
      const giftSteps = (this.config.steps || []).filter((step) => step.giftProduct);
      const maxMutations = Math.max(1, giftSteps.length * 2 + 1);

      for (let attempt = 0; attempt < maxMutations; attempt += 1) {
        const totalWithoutGifts = this.calculateTotalWithoutGifts(currentCart);
        let mutation = null;

        for (const step of giftSteps) {
          const giftInCart = currentCart.items.find((item) =>
            item.variant_id === step.giftProduct.variantId &&
            item.properties &&
            item.properties._gift === step.id
          );

          const thresholdReached = totalWithoutGifts >= step.threshold;

          if (thresholdReached && !giftInCart) {
            mutation = { type: 'add', step };
            break;
          }

          if (!thresholdReached && giftInCart) {
            mutation = { type: 'remove', key: giftInCart.key };
            break;
          }
        }

        if (!mutation) break;

        currentCart = mutation.type === 'add'
          ? await this.addGiftProduct(mutation.step)
          : await this.removeGiftProduct(mutation.key);

        changed = true;
      }
    } finally {
      this.isHandlingGifts = false;
    }

    return { cart: currentCart, changed };
  }
  async addGiftProduct(step) {
    return this.requestAddAndCart([{
      id: step.giftProduct.variantId,
      quantity: 1,
      properties: {
        _gift: step.id,
        _gift_label: step.label
      }
    }]);
  }
  async removeGiftProduct(key) {
    return this.requestChange(key, 0);
  }
  updateProgressBars(totalPrice) {
    const steps = this.config.steps || [];
    if (steps.length === 0) return;

    const ANIMATION_DURATION = 500; // 0.5s in ms

    // Sort steps by threshold
    const sortedSteps = [...steps].sort((a, b) => a.threshold - b.threshold);

    // Find active bar index (the one currently filling/emptying)
    let activeBarIndex = 0;
    for (let i = 0; i < sortedSteps.length; i++) {
      if (totalPrice < sortedSteps[i].threshold) {
        activeBarIndex = i;
        break;
      }
      if (i === sortedSteps.length - 1) {
        activeBarIndex = sortedSteps.length; // All complete
      }
    }

    // Detect if we crossed a threshold
    const previousActiveIndex = this.previousActiveBarIndex ?? activeBarIndex;
    const thresholdCrossed = activeBarIndex !== previousActiveIndex;
    const isGoingUp = activeBarIndex > previousActiveIndex;
    this.previousActiveBarIndex = activeBarIndex;

    // Clear any pending timeouts
    if (this.progressTimeouts) {
      this.progressTimeouts.forEach(t => clearTimeout(t));
    }
    this.progressTimeouts = [];

    // Calculate progress for each step
    let allReached = true;
    let firstUnreachedStep = null;

    sortedSteps.forEach((step, index) => {
      const reached = totalPrice >= step.threshold;
      if (!reached && !firstUnreachedStep) {
        allReached = false;
        firstUnreachedStep = step;
      }

      // Calculate the start and end thresholds for this bar
      const startThreshold = index === 0 ? 0 : sortedSteps[index - 1].threshold;
      const endThreshold = step.threshold;
      const range = endThreshold - startThreshold;

      // Update fill for this step
      const fill = this.sidecart.querySelector(`[data-sidecart-fill="${step.id}"]`);
      if (fill) {
        let stepProgress = 0;

        if (index < activeBarIndex) {
          stepProgress = 100;
        } else if (index > activeBarIndex) {
          stepProgress = 0;
        } else {
          const current = totalPrice - startThreshold;
          stepProgress = Math.min(100, Math.max(0, (current / range) * 100));
        }

        // Only apply delay when crossing a threshold
        let delay = 0;
        if (thresholdCrossed) {
          if (isGoingUp) {
            // Going up: lower bars fill first, then higher bars follow
            // Bars at index > previousActiveIndex wait for previous ones to complete
            if (index > previousActiveIndex) {
              delay = (index - previousActiveIndex) * ANIMATION_DURATION;
            }
          } else {
            // Going down: higher bars empty first, then lower bars follow
            // Highest bar (index = length-1) empties immediately
            // Lower bars wait for higher ones to finish
            const highestIndex = sortedSteps.length - 1;
            delay = (highestIndex - index) * ANIMATION_DURATION;
          }
        }

        const timeout = setTimeout(() => {
          fill.style.transform = `scaleX(${stepProgress / 100})`;
        }, delay);
        this.progressTimeouts.push(timeout);
      }

      // Update label
      const label = this.sidecart.querySelector(`[data-sidecart-label="${step.id}"]`);
      if (label) {
        label.classList.toggle('sidecart__progress-label--active', reached);
      }
    });

    // Update text
    const progressText = this.sidecart.querySelector('[data-sidecart-progress-text]');
    if (progressText) {
      if (allReached) {
        progressText.textContent = this.config.completionMessage;
      } else if (firstUnreachedStep) {
        const remaining = Math.max(0, firstUnreachedStep.threshold - totalPrice);
        progressText.textContent = firstUnreachedStep.message.replace('{amount}', this.formatMoney(remaining));
      }
    }
  }

  updateEmptyState(itemCount) {
    const productsEl = this.sidecart.querySelector('[data-sidecart-products]');
    let emptyEl = this.sidecart.querySelector('[data-sidecart-empty]');

    const crosssellTitle = this.sidecart.querySelector('[data-crosssell-title]');

    if (itemCount === 0) {
      if (productsEl) {
        productsEl.style.display = 'none';
      }
      if (!emptyEl && this.content) {
        emptyEl = document.createElement('div');
        emptyEl.className = 'sidecart__empty';
        emptyEl.setAttribute('data-sidecart-empty', '');
        emptyEl.innerHTML = `
          <p class="sidecart__empty-text p-extra-large text-center">${wSidecartText('empty')}</p>
          <a href="/collections/all" class="button button--primary">${wSidecartText('discover')}</a>
        `;
        this.content.appendChild(emptyEl);
      }
      if (emptyEl) {
        emptyEl.style.display = '';
      }
      if (crosssellTitle) {
        crosssellTitle.textContent = crosssellTitle.getAttribute('data-crosssell-title-empty');
      }
    } else {
      if (productsEl) {
        productsEl.style.display = '';
      }
      if (emptyEl) {
        emptyEl.style.display = 'none';
      }
      if (crosssellTitle) {
        crosssellTitle.textContent = crosssellTitle.getAttribute('data-crosssell-title');
      }
    }
  }

  formatMoney(cents) {
    // Utiliser la fonction globale formatMoney du thème si disponible
    if (typeof formatMoney === 'function') {
      return formatMoney(cents, this.config.moneyFormat);
    }
    // Fallback simple
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  initSwiper() {
    const swiperEl = this.sidecart.querySelector('[data-sidecart-swiper]');
    if (!swiperEl || typeof Swiper === 'undefined') return;

    // Détruire l'ancien swiper si existe
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    const paginationEl = this.sidecart.querySelector('.sidecart__crosssell-pagination');

    this.swiper = new Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 12,
      pagination: {
        el: paginationEl,
        clickable: true
      }
    });
  }

  async loadRecommendations(productIdToUse) {
    const wrapper = this.sidecart.querySelector('.sidecart__crosssell-slider .swiper-wrapper');
    if (!wrapper || !productIdToUse) return;

    const productId = String(productIdToUse);
    if (
      productId === String(this.loadedRecommendationsProductId || '') ||
      productId === String(this.loadingRecommendationsProductId || '')
    ) {
      return;
    }

    this.loadingRecommendationsProductId = productId;
    const requestId = ++this.recommendationsRequestId;

    try {
      const url = `${this.config.routes.recommendations}?section_id=w-sidecart-recommendations&product_id=${productId}&limit=${this.config.crossSellCount}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Recommendations fetch failed (${response.status})`);
      }

      const html = await response.text();
      if (requestId !== this.recommendationsRequestId) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const slides = doc.querySelectorAll('.swiper-slide');

      wrapper.innerHTML = '';

      slides.forEach((slide) => {
        wrapper.appendChild(slide.cloneNode(true));
      });

      this.loadedRecommendationsProductId = productId;

      setTimeout(() => {
        if (requestId === this.recommendationsRequestId) {
          this.initSwiper();
        }
      }, 100);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      if (requestId === this.recommendationsRequestId) {
        this.loadingRecommendationsProductId = null;
      }
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  window.wSidecart = new WSidecart();
});

// Global function to open sidecart
window.openSidecart = async function(options = {}) {
  const shouldRefresh = options.refresh !== false;

  if (window.wSidecart) {
    if (window.wSidecart.isDirty) {
      await window.wSidecart.refresh();
      window.wSidecart.open();
      return;
    }

    window.wSidecart.open();

    if (shouldRefresh) {
      window.wSidecart.refresh();
    }
  } else {
    // Si pas encore initialisé, ouvrir directement via DOM
    const sidecart = document.querySelector('[data-sidecart]');
    if (sidecart) {
      sidecart.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('sidecart-open');
      document.body.classList.add('sidecart-open');
    }
  }
};

// Global function to close sidecart
window.closeSidecart = function() {
  if (window.wSidecart) {
    window.wSidecart.close();
  } else {
    const sidecart = document.querySelector('[data-sidecart]');
    if (sidecart) {
      sidecart.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('sidecart-open');
      document.body.classList.remove('sidecart-open');
    }
  }
};

// Global function to toggle sidecart
window.toggleSidecart = async function() {
  if (window.wSidecart) {
    if (window.wSidecart.isOpen()) {
      window.wSidecart.close();
      return;
    }

    if (window.wSidecart.isDirty) {
      await window.wSidecart.refresh();
    }

    window.wSidecart.open();
  } else {
    const sidecart = document.querySelector('[data-sidecart]');
    if (sidecart) {
      const isOpen = sidecart.getAttribute('aria-hidden') === 'false';
      sidecart.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
      document.documentElement.classList.toggle('sidecart-open', !isOpen);
      document.body.classList.toggle('sidecart-open', !isOpen);
    }
  }
};
